import { createImageAdapter } from '../../core/image';
import type { GeneratedImage, ResolvedImageConfig } from '../../core/image';
import { createProviderAdapter, createServerBackedAdapter, SERVER_PROVIDER_ID } from '../../core/providers';
import type { ProviderAdapter, ProviderDeps, ResolvedProviderConfig } from '../../core/providers';
import { wrapProviderFetch } from '../../core/providers/providerFetch';
import { createTtsAdapter } from '../../core/tts';
import type { ResolvedTtsConfig, TtsAdapter, TtsRequest, TtsResult } from '../../core/tts';
import { generateServerImage } from '../chat/serverImage';
import type { ServerImageRequest } from '../chat/serverImage';
import { createServerTtsAdapter } from '../mimo/adapter';
import type { ServerTtsAdapterOptions } from '../mimo/adapter';

export interface GatewayImageRequest {
  config?: ResolvedImageConfig | null;
  serverModel?: string | null;
  prompt: string;
  signal?: AbortSignal;
  automation?: boolean;
}

export interface GatewaySpeechRequest {
  config: ResolvedTtsConfig;
  request: TtsRequest;
}

/**
 * The single application gateway for model dispatch. Feature code must not
 * instantiate provider/image/TTS adapters directly; this class decides whether
 * a call is official or user-configured and is shared by chat and automation.
 */
export class ModelGateway {
  createChatAdapter(config: ResolvedProviderConfig, deps?: ProviderDeps): ProviderAdapter {
    const adapter = config.id === SERVER_PROVIDER_ID
      ? createServerBackedAdapter(config, deps)
      : createProviderAdapter(config, deps);
    if (config.id === SERVER_PROVIDER_ID) return adapter;
    return {
      id: adapter.id,
      kind: adapter.kind,
      listModels: () => adapter.listModels(),
      chat: request => request.intimacy
        ? createProviderAdapter(config, { ...deps, fetch: wrapProviderFetch(deps?.fetch ?? globalThis.fetch.bind(globalThis), request.intimacy) }).chat(request)
        : adapter.chat(request),
    };
  }

  async listModels(config: ResolvedProviderConfig, deps?: ProviderDeps): Promise<string[]> {
    return this.createChatAdapter(config, deps).listModels();
  }

  generateImage(request: GatewayImageRequest): Promise<GeneratedImage[]> {
    if (request.serverModel) {
      return generateServerImage({
        prompt: request.prompt,
        model: request.serverModel,
        signal: request.signal ?? new AbortController().signal,
        ...(request.automation === true ? { automation: true } : {}),
      } satisfies ServerImageRequest);
    }
    if (!request.config) return Promise.reject(new Error('生图配置不可用'));
    return createImageAdapter(request.config).generate({
      prompt: request.prompt,
      ...(request.signal ? { signal: request.signal } : {}),
    });
  }

  speak(request: GatewaySpeechRequest): Promise<TtsResult> {
    return createTtsAdapter(request.config).speak(request.request);
  }

  createOfficialTtsAdapter(options: ServerTtsAdapterOptions): TtsAdapter {
    return createServerTtsAdapter(options);
  }
}

export const modelGateway = new ModelGateway();
